// src/store/useNotificationStore.js
import { create } from "zustand";
import { axiosInstancev1 } from "../lib/axiosInstance";
import { toast } from "react-hot-toast";
import useAuthStore from "./useAuthStore";

const useNotificationStore = create((set, get) => ({
    notifications: [],
    pagination: null,
    unreadCount: 0,
    isLoading: false,
    error: null,

    // ============================================================
    // FETCH NOTIFICATIONS
    // ============================================================
    fetchNotifications: async (is_read = null) => {
        set({ isLoading: true, error: null });

        const { token, user } = useAuthStore.getState();

        if (!token) {
            set({ isLoading: false, error: "Not authenticated" });
            return;
        }

        try {
            const params = new URLSearchParams();
            if (is_read !== null) params.append("is_read", is_read);

            // Prepare headers - only add x-builder-id for builders
            const headers = {
                Authorization: `Bearer ${token}`,
            };

            // Only add x-builder-id header for builders
            const builderId = user?.builder_profile_id || user?.builderId;
            if (builderId) {
                headers["x-builder-id"] = builderId;
            }

            const response = await axiosInstancev1.get(
                `/notifications?${params.toString()}`,
                { headers }
            );

            if (response.data.success) {
                set({
                    notifications: response.data.data,
                    pagination: response.data.pagination,
                    isLoading: false
                });
            } else {
                throw new Error(response.data.message);
            }

        } catch (error) {
            const errorMsg =
                error.response?.data?.message ||
                "Error fetching notifications.";

            toast.error(errorMsg);

            set({
                isLoading: false,
                error: errorMsg,
                notifications: []
            });
        }
    },

    // ============================================================
    // FETCH UNREAD COUNT
    // ============================================================
    fetchUnreadCount: async () => {
        const { token, user } = useAuthStore.getState();

        if (!token) return;

        try {
            // Prepare headers - only add x-builder-id for builders
            const headers = {
                Authorization: `Bearer ${token}`,
            };

            const builderId = user?.builder_profile_id || user?.builderId;
            if (builderId) {
                headers["x-builder-id"] = builderId;
            }

            const response = await axiosInstancev1.get(
                "/notifications/unread-count",
                { headers }
            );

            if (response.data.success) {
                set({ unreadCount: response.data.unreadCount });
            }
        } catch (error) {
            console.error("Could not fetch unread count:", error);
        }
    },

    // ============================================================
    // MARK AS READ
    // ============================================================
    markAsRead: async (notificationId) => {
        const { token, user } = useAuthStore.getState();

        if (!token) return;

        const previous = get().notifications;

        set(state => ({
            notifications: state.notifications.map(n =>
                n.id === notificationId ? { ...n, is_read: 1 } : n
            )
        }));

        try {
            // Prepare headers - only add x-builder-id for builders
            const headers = {
                Authorization: `Bearer ${token}`,
            };

            const builderId = user?.builder_profile_id || user?.builderId;
            if (builderId) {
                headers["x-builder-id"] = builderId;
            }

            const res = await axiosInstancev1.patch(
                `/notifications/${notificationId}/read`,
                {},
                { headers }
            );

            if (!res.data.success) throw new Error(res.data.message);

            get().fetchUnreadCount();

        } catch (error) {
            toast.error(error.message || "Failed to mark as read.");
            set({ notifications: previous });
        }
    },

    // ============================================================
    // MARK AS UNREAD
    // ============================================================
    markAsUnread: async (notificationId) => {
        const { token, user } = useAuthStore.getState();

        if (!token) return;

        const previous = get().notifications;

        set(state => ({
            notifications: state.notifications.map(n =>
                n.id === notificationId ? { ...n, is_read: 0 } : n
            )
        }));

        try {
            // Prepare headers - only add x-builder-id for builders
            const headers = {
                Authorization: `Bearer ${token}`,
            };

            const builderId = user?.builder_profile_id || user?.builderId;
            if (builderId) {
                headers["x-builder-id"] = builderId;
            }

            const res = await axiosInstancev1.patch(
                `/notifications/${notificationId}/unread`,
                {},
                { headers }
            );

            if (!res.data.success) throw new Error(res.data.message);

            get().fetchUnreadCount();

        } catch (error) {
            toast.error(error.message || "Failed to mark as unread.");
            set({ notifications: previous });
        }
    },

    // ============================================================
    // MARK ALL AS READ
    // ============================================================
    markAllAsRead: async () => {
        const { token, user } = useAuthStore.getState();

        if (!token) return;

        const previous = get().notifications;

        set({
            notifications: previous.map(n => ({ ...n, is_read: 1 })),
            unreadCount: 0
        });

        try {
            // Prepare headers - only add x-builder-id for builders
            const headers = {
                Authorization: `Bearer ${token}`,
            };

            const builderId = user?.builder_profile_id || user?.builderId;
            if (builderId) {
                headers["x-builder-id"] = builderId;
            }

            const res = await axiosInstancev1.post(
                "/notifications/mark-all-read",
                {},
                { headers }
            );

            if (!res.data.success) throw new Error(res.data.message);

            toast.success(res.data.message);
            get().fetchNotifications();

        } catch (error) {
            toast.error(error.message || "Failed to mark all as read.");
            set({ notifications: previous });
            get().fetchUnreadCount();
        }
    }
}));

export default useNotificationStore;

